import { lazy } from "react";
import { Navigate, type RouteObject } from "react-router";

import { AdminLayout } from "./admin/AdminLayout";
import { RequireAuth, RequirePermission } from "./auth/session";
import { AppShell } from "./layouts/AppShell";
import { NotFoundPage } from "./pages/NotFoundPage";
import { HomePage, PortalHomePage } from "./pages/PortalHomePage";
import { SignInPage } from "./pages/SignInPage";
import { NewTicketPage } from "./tickets/NewTicketPage";
import { TicketPage } from "./tickets/TicketPage";
import { TicketsPage } from "./tickets/TicketsPage";

// Admin, reports and search load on first visit, so most people (employees) never download them.
const AnnouncementsPage = lazy(() =>
  import("./admin/AnnouncementsPage").then((m) => ({ default: m.AnnouncementsPage })),
);
const ServiceStatusPage = lazy(() =>
  import("./admin/ServiceStatusPage").then((m) => ({ default: m.ServiceStatusPage })),
);
const DeliveriesPage = lazy(() => import("./admin/DeliveriesPage").then((m) => ({ default: m.DeliveriesPage })));
const EmailReviewPage = lazy(() => import("./admin/EmailReviewPage").then((m) => ({ default: m.EmailReviewPage })));
const GroupDetailPage = lazy(() => import("./admin/GroupDetailPage").then((m) => ({ default: m.GroupDetailPage })));
const GroupsPage = lazy(() => import("./admin/GroupsPage").then((m) => ({ default: m.GroupsPage })));
const PeoplePage = lazy(() => import("./admin/PeoplePage").then((m) => ({ default: m.PeoplePage })));
const SavedRepliesPage = lazy(() => import("./admin/SavedRepliesPage").then((m) => ({ default: m.SavedRepliesPage })));
const ServicesPage = lazy(() => import("./admin/ServicesPage").then((m) => ({ default: m.ServicesPage })));
const SettingsPage = lazy(() => import("./admin/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const ReportsPage = lazy(() => import("./reports/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const ReportTicketsPage = lazy(() => import("./reports/ReportTicketsPage").then((m) => ({ default: m.ReportTicketsPage })));
const SearchPage = lazy(() => import("./search/SearchPage").then((m) => ({ default: m.SearchPage })));

// Queues, the dashboard and admin tables use the wide layout (see AppShell). Some pages are
// wide only for the people who see a big table there.
const WIDE = { wide: true };

/**
 * Every page. Hiding a link is only a convenience: the backend checks permissions on every
 * request, and RequirePermission just explains why an area is unavailable.
 */
export const routes: RouteObject[] = [
  { path: "/signin", element: <SignInPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, handle: WIDE, element: <HomePage /> },
          { path: "help", handle: WIDE, element: <PortalHomePage /> },
          // Your tickets and the workspace are one page (staff see both; employees their own).
          { path: "tickets", handle: WIDE, element: <TicketsPage /> },
          { path: "tickets/new", element: <NewTicketPage /> },
          { path: "tickets/:reference", element: <TicketPage /> },
          { path: "workspace", element: <Navigate to="/tickets" replace /> },
          { path: "search", element: <SearchPage /> },
          {
            path: "reports",
            handle: WIDE,
            element: (
              <RequirePermission permission="view_reports">
                <ReportsPage />
              </RequirePermission>
            ),
          },
          {
            path: "reports/tickets",
            handle: WIDE,
            element: (
              <RequirePermission permission="view_reports">
                <ReportTicketsPage />
              </RequirePermission>
            ),
          },
          {
            path: "admin",
            handle: WIDE,
            element: (
              <RequirePermission permission="manage_people_and_roles">
                <AdminLayout />
              </RequirePermission>
            ),
            children: [
              { index: true, element: <Navigate to="people" replace /> },
              { path: "people", element: <PeoplePage /> },
              { path: "groups", element: <GroupsPage /> },
              { path: "groups/:groupId", element: <GroupDetailPage /> },
              {
                path: "services",
                element: (
                  <RequirePermission permission="manage_catalogue">
                    <ServicesPage />
                  </RequirePermission>
                ),
              },
              {
                path: "service-status",
                element: (
                  <RequirePermission permission="post_service_updates">
                    <ServiceStatusPage />
                  </RequirePermission>
                ),
              },
              {
                path: "announcements",
                element: (
                  <RequirePermission permission="post_service_updates">
                    <AnnouncementsPage />
                  </RequirePermission>
                ),
              },
              {
                path: "deliveries",
                element: (
                  <RequirePermission permission="view_delivery_failures">
                    <DeliveriesPage />
                  </RequirePermission>
                ),
              },
              {
                path: "settings",
                element: (
                  <RequirePermission permission="manage_settings">
                    <SettingsPage />
                  </RequirePermission>
                ),
              },
              {
                path: "saved-replies",
                element: (
                  <RequirePermission permission="manage_settings">
                    <SavedRepliesPage />
                  </RequirePermission>
                ),
              },
              {
                path: "email",
                element: (
                  <RequirePermission permission="manage_settings">
                    <EmailReviewPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
];
